import { useState } from "react";
import { FixaModal } from "../../../shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "../../../shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "../../../shared/wrappers/FixaModal/FixaModalHeader";
import { useTranslation } from "@coworker/locales";
import { StoreTeam } from "@coworker/types/lib/storeteam";
import { TeamStatus } from "../../../shared/types/team";
import { TeamModalInputs } from "./TeamModalInputs";
import { NewTeamDataType } from "../../types/TeamMutationTypes";
import TeamModalFooter from "./TeamModalFooter";

interface TeamModalProps {
  visible: boolean;
  close: () => void;
  storeId: string;
  hasActiveProductQualityTeam: boolean;
  submitButtonText?: string;
  onSubmit: (newTeamData: NewTeamDataType) => void | Promise<void>;
  isSubmitting: boolean;
  initialData?: StoreTeam | null;
  handleDelete?: () => void;
  isDeleting?: boolean;
}

const TeamModal = ({
  visible,
  close,
  storeId,
  hasActiveProductQualityTeam,
  onSubmit,
  isSubmitting,
  initialData,
  handleDelete,
  isDeleting,
}: TeamModalProps) => {
  const { t } = useTranslation();

  const defaultTeamData = {
    store_id: storeId,
    status: TeamStatus.ACTIVE,
  };

  const [overrides, setOverrides] = useState<{
    name?: string;
    is_product_quality?: boolean;
  }>({});

  const formValues = {
    name: overrides.name ?? initialData?.name ?? "",
    is_product_quality:
      overrides.is_product_quality ?? initialData?.is_product_quality ?? false,
  };

  const submitTranslationText = initialData
    ? t("updateNameString", { name: initialData.name })
    : t("createNameString", { name: formValues.name });

  const hasProductQualityTeamError =
    !initialData?.is_product_quality &&
    hasActiveProductQualityTeam &&
    formValues.is_product_quality;

  const handleChange = (
    field: keyof TeamModalInputs,
    value: TeamModalInputs[typeof field]
  ) => {
    setOverrides((prev) => ({
      ...prev,
      [field === "isProductQualityTeam" ? "is_product_quality" : "name"]: value,
    }));
  };

  const handleSubmit = () => {
    const newTeamData = {
      ...defaultTeamData,
      ...formValues,
      updated_at: Date.now(),
      ...(initialData
        ? { team_id: initialData.id, created_at: initialData.created_at }
        : { created_at: Date.now() }),
    };
    onSubmit(newTeamData);
  };

  return (
    <FixaModal handleCloseBtn={close} visible={visible}>
      <FixaSheets
        labelledById="teams-management-team-modal-header"
        alignment="right"
        preserveAlignment={false}
        size="small"
        fullSize
        header={
          <FixaModalHeader
            id="teams-management-team-modal-header"
            title={initialData ? t("editTeamString") : t("addTeamString")}
            floating={false}
          />
        }
        footer={
          <TeamModalFooter
            hasProductQualityTeamError={hasProductQualityTeamError}
            handleSubmit={handleSubmit}
            handleDelete={handleDelete}
            disableDelete={isDeleting || isSubmitting}
            disableSubmit={
              isDeleting ||
              isSubmitting ||
              hasProductQualityTeamError ||
              !formValues.name.trim()
            }
            submitTranslationText={submitTranslationText}
          />
        }
      >
        <TeamModalInputs
          onChange={handleChange}
          teamName={formValues.name}
          isProductQualityTeam={formValues.is_product_quality}
        />
      </FixaSheets>
    </FixaModal>
  );
};

export default TeamModal;
